import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageBreadcrumbs from "../../Base/PageBreadcrumbs";

const DOCS_BASE = process.env.PUBLIC_URL || "";

type DocVariant = "user" | "technical";

const DOC_URLS: Record<DocVariant, string> = {
  user: `${DOCS_BASE}/docs/ADMIN_USER.md`,
  technical: `${DOCS_BASE}/docs/ADMIN_TECHNICAL.md`,
};

function resolveImageSrc(src: string): string {
  if (!src || src.startsWith("http") || src.startsWith("data:")) return src;
  if (src.startsWith("/")) return `${DOCS_BASE}${src}`;
  return `${DOCS_BASE}/docs/${src}`;
}

const AdminDocsPage: React.FC = () => {
  const theme = useTheme();
  const [variant, setVariant] = useState<DocVariant>("user");
  const [content, setContent] = useState<Record<DocVariant, string | null>>({
    user: null,
    technical: null,
  });
  const [loading, setLoading] = useState<Record<DocVariant, boolean>>({
    user: true,
    technical: true,
  });
  const [error, setError] = useState<Record<DocVariant, string | null>>({
    user: null,
    technical: null,
  });

  useEffect(() => {
    const fetchDoc = async (v: DocVariant) => {
      setLoading((prev) => ({ ...prev, [v]: true }));
      setError((prev) => ({ ...prev, [v]: null }));
      try {
        const res = await fetch(DOC_URLS[v]);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        setContent((prev) => ({ ...prev, [v]: text }));
      } catch (e) {
        setError((prev) => ({
          ...prev,
          [v]: e instanceof Error ? e.message : "Erreur de chargement",
        }));
      } finally {
        setLoading((prev) => ({ ...prev, [v]: false }));
      }
    };

    fetchDoc("user");
    fetchDoc("technical");
  }, []);

  const currentContent = content[variant];
  const currentLoading = loading[variant];
  const currentError = error[variant];

  const markdownSx = {
    "& h1": {
      fontSize: "1.75rem",
      fontWeight: 700,
      mt: 4,
      mb: 2,
      pb: 1,
      borderBottom: 2,
      borderColor: "primary.main",
      color: "text.primary",
    },
    "& h1:first-of-type": { mt: 0 },
    "& h2": { fontSize: "1.4rem", fontWeight: 600, mt: 3, mb: 1.5, color: "text.primary" },
    "& h3": { fontSize: "1.15rem", fontWeight: 600, mt: 2.5, mb: 1, color: "text.secondary" },
    "& p": { mb: 1.5, lineHeight: 1.7 },
    "& ul, & ol": { pl: 3, mb: 1.5, "& li": { mb: 0.5 } },
    "& hr": { my: 3, borderColor: "divider" },
    "& strong": { fontWeight: 600 },
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      mb: 2,
      borderRadius: 1,
      overflow: "hidden",
      boxShadow: 1,
    },
    "& th, & td": {
      border: "1px solid",
      borderColor: "divider",
      px: 2,
      py: 1.5,
      textAlign: "left",
    },
    "& th": { bgcolor: "action.hover", fontWeight: 600, fontSize: "0.9rem" },
    "& tr:nth-of-type(even)": { bgcolor: "action.hover" },
    "& code": {
      bgcolor: "action.selected",
      px: 0.75,
      py: 0.25,
      borderRadius: 0.5,
      fontSize: "0.9em",
      fontFamily: "monospace",
    },
    "& pre": {
      overflow: "auto",
      p: 2,
      bgcolor: "action.hover",
      borderRadius: 1,
      border: "1px solid",
      borderColor: "divider",
      "& code": { bgcolor: "transparent", p: 0 },
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
      borderRadius: 1,
      boxShadow: 2,
      my: 2,
      display: "block",
    },
    "& blockquote": {
      borderLeft: 4,
      borderColor: "primary.main",
      pl: 2,
      pr: 1,
      py: 0.5,
      my: 2,
      bgcolor: "action.hover",
      fontStyle: "italic",
    },
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <PageBreadcrumbs />
      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
        Documentation
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Guide utilisateur (tous les écrans) et documentation technique (sources de données, Cloud Functions).
      </Typography>
      <Paper variant="outlined" sx={{ mb: 3, overflow: "hidden" }}>
        <Tabs
          value={variant}
          onChange={(_, v) => setVariant(v as DocVariant)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
            "& .MuiTab-root": { fontWeight: 600 },
          }}
        >
          <Tab label="Guide utilisateur" value="user" />
          <Tab label="Documentation technique" value="technical" />
        </Tabs>
        <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, minHeight: 400 }}>
          {currentLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {currentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Impossible de charger le document : {currentError}
            </Alert>
          )}
          {!currentLoading && currentContent && (
            <Box component="article" sx={markdownSx}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ src, alt }) => (
                    <img
                      src={src ? resolveImageSrc(src) : ""}
                      alt={alt ?? ""}
                      loading="lazy"
                    />
                  ),
                }}
              >
                {currentContent}
              </ReactMarkdown>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminDocsPage;
